import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register the components for Chart.js
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

interface ChartProps {
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor?: string;
      backgroundColor?: string; // Use only one backgroundColor
      borderWidth?: number;
    }[];
  };
  options?: any;
  animationDuration?: number; // Duration of animation in milliseconds
}

const Chart: React.FC<ChartProps> = ({ data, options, animationDuration = 2000 }) => {
  const [visibleData, setVisibleData] = useState<{ labels: string[]; datasets: any[] }>({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    const totalPoints = data.labels.length;
    let currentPointIndex = 0;

    const interval = setInterval(() => {
      // Check if all points have been displayed
      if (currentPointIndex < totalPoints) {
        // Update the visible data
        setVisibleData({
          labels: data.labels.slice(0, currentPointIndex + 1),
          datasets: data.datasets.map((dataset) => ({
            ...dataset,
            data: dataset.data.slice(0, currentPointIndex + 1),
          })),
        });
        currentPointIndex++;
      } else {
        clearInterval(interval); // Stop when all points are displayed
      }
    }, animationDuration / totalPoints); // Spread out the points over the total duration

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [data, animationDuration]);

  // Default options if none are provided
  const defaultOptions = {
    responsive: true,
    animations: {
      tension: {
        duration: 300,
        easing: 'linear',
        from: 0.5,
        to: 0,
        loop: false,
      },
      // Optional: if you want to animate points separately
      points: {
        radius: {
          duration: 300,
          easing: 'linear',
          from: 0,
          to: 5, // The radius of the points after animation
          loop: false,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false, // Hide the x-axis grid lines
        },
      },
      y: {
        grid: {
          display: false, // Hide the y-axis grid lines
        },
      },
    },
    elements: {
      line: {
        tension: 0.5, // This creates a smooth curve
      },
      point: {
        radius: 5, // Default radius for points
      },
    },
  };

  return (
    <div className='mt-4'>
      <Line data={{ labels: visibleData.labels, datasets: visibleData.datasets }} options={{ ...defaultOptions, ...options }} />
    </div>
  );
};

export default Chart;
